/**
 * Silent create + related-lookup tools for the in-app assistant (request 0295).
 *
 * Confirmation is the only gate: `kind: 'write'` always confirms; there is no
 * preference that turns it off. Relation ids must come from
 * `data.findRelatedOptions` for that (entity, field) pair.
 */
import type { AssistantTool, AssistantToolResult } from '@rammetec/app-kit/assistant'
import type { FieldDescriptor } from '@rammetec/data-access'
import { entityRegistry } from '@rammetec/data-access'
import { rtLogService } from '@rammetec/core'
import {
  EntityValidationError,
  ValidationException,
  getDescriptorForRule,
} from '@syscolect/domain'
import {
  LOOKUP_CANDIDATE_CAP,
  coerceFormFieldValue,
  searchLookupCandidates,
  type LookupCandidate,
  type RelationFormField,
  type WritableFormField,
} from '@/shared/crud/assistantFormTools'
import {
  SERVICE_FILLED_NAMES,
  resolveImportableFields,
} from '@/shared/import/engine'
import { entityUsePoint } from '@/shared/forms/useAutoCodeConfig'
import { nextCode } from '@/services/sequenceApi'
import { loadCreatableService } from './assistantCreateServices'
import type { AssistantQueryableEntity } from './assistantEntityCatalog'
import type { DescribeFieldMeta } from '@/shared/crud/assistantDescribeFields'

/** Cache key: entity label + field label (relation discipline per H5). */
export type RelatedOptionsCache = Map<string, readonly LookupCandidate[]>

export function relatedCacheKey(entidade: string, campo: string): string {
  return `${entidade}\0${campo}`
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function suggestLabels(all: readonly string[], query: string, cap = 5): string[] {
  const q = query.trim().toLowerCase()
  if (!q) return all.slice(0, cap)
  const scored = all
    .map((label) => {
      const l = label.toLowerCase()
      let score = 0
      if (l === q) score = 3
      else if (l.startsWith(q)) score = 2
      else if (l.includes(q)) score = 1
      return { label, score }
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
  return scored.slice(0, cap).map((x) => x.label)
}

function formatCandidateLine(c: LookupCandidate): string {
  return `${c.label} (id=${c.id})`
}

/**
 * Fields the model may set on create, plus FK relations (lookup via
 * `data.findRelatedOptions`). Skips service-filled / autoNumber / audit FKs.
 * Thin wrapper over {@link resolveImportableFields} (import engine — spec 0051 D2).
 */
export function resolveCreatableFields(entity: string): {
  writable: WritableFormField[]
  relations: RelationFormField[]
  autoNumberField?: FieldDescriptor
} {
  const descriptor = getDescriptorForRule(entity, 'assistant-create')
  if (!descriptor) {
    return { writable: [], relations: [] }
  }

  const importable = resolveImportableFields(descriptor)
  const relations: RelationFormField[] = []
  const writable: WritableFormField[] = []
  let autoNumberField: FieldDescriptor | undefined

  for (const field of importable) {
    if (field.autoNumber) {
      autoNumberField = field.descriptor
      continue
    }
    if (field.relation) {
      const descKey =
        field.relation.matchKeys.find((k) => k.key === 'description') ??
        field.relation.matchKeys[field.relation.matchKeys.length - 1]
      relations.push({
        name: field.name,
        label: field.label,
        targetEntity: field.relation.targetEntity,
        displayField: descKey?.field ?? 'description',
      })
      continue
    }
    writable.push({
      name: field.name,
      label: field.label,
      descriptor: field.descriptor,
    })
  }

  return { writable, relations, ...(autoNumberField ? { autoNumberField } : {}) }
}

/** Meta for `data.describeFields`: obrigatório + preenchido pelo serviço. */
export function buildDescribeFieldMeta(
  entityName: string,
  creatable: boolean,
): Map<string, DescribeFieldMeta> {
  const meta = new Map<string, DescribeFieldMeta>()
  if (!entityRegistry.has(entityName)) return meta
  const fields = entityRegistry.get(entityName).fields
  for (const field of fields) {
    const alwaysService =
      SERVICE_FILLED_NAMES.has(field.name) || field.autoNumber === true
    const serviceFilled = creatable && alwaysService
    const required = field.required === true && !alwaysService
    if (!required && !serviceFilled) continue
    meta.set(field.name, {
      ...(required ? { required: true } : {}),
      ...(serviceFilled ? { serviceFilled: true } : {}),
    })
  }
  return meta
}

function pickRecordLabel(row: Record<string, unknown>): string {
  for (const key of ['displayName', 'description', 'reference', 'name', 'code']) {
    const v = row[key]
    if (typeof v === 'string' && v.trim()) return v.trim()
    if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  }
  const id = row.id
  if (typeof id === 'string' && id.trim()) return id.trim()
  return '—'
}

function translateValidationErrors(
  errors: Record<string, string>,
  entity: string,
): string {
  const descriptor = getDescriptorForRule(entity, 'assistant-create-errors')
  const labelOf = (name: string): string =>
    descriptor?.fields.find((f) => f.name === name)?.label ?? name
  const parts = Object.entries(errors).map(
    ([name, msg]) => `${labelOf(name)}: ${msg}`,
  )
  if (parts.length === 1) return parts[0]!
  return parts.join('; ')
}

function formatUniquenessError(
  error: ValidationException,
  entity: string,
): string {
  const details = error.details ?? {}
  const descriptor = getDescriptorForRule(entity, 'assistant-create-unique')
  const codeField = descriptor?.fields.find((f) => f.unique === true)
  const fieldLabel = codeField?.label ?? 'Código'
  const code = details.code
  const conflictId = details.conflictId
  const bits = [error.message]
  bits.push(`Campo: ${fieldLabel}`)
  if (code !== undefined && code !== null) bits.push(`valor ${String(code)}`)
  if (typeof conflictId === 'string' && conflictId.trim()) {
    bits.push(`registro existente id=${conflictId.trim()}`)
  }
  return bits.join('. ')
}

export interface CreateRecordItem {
  campo: string
  valor: string
}

export interface CreateRecordParams {
  entidade: string
  valores: CreateRecordItem[]
}

export interface FindRelatedParams {
  entidade: string
  campo: string
  busca: string
}

export interface AssistantCreateToolsDeps {
  getOrgId: () => string | null
  getAuditReason: () => string
  /** Optional override for tests — defaults to {@link loadCreatableService}. */
  loadService?: (entity: string) => Promise<
    | {
        create: (
          orgId: string,
          data: Record<string, unknown>,
          ctx?: undefined,
          opts?: { audit?: { source: 'assistant'; reason?: string } },
        ) => Promise<{ id: string } & Record<string, unknown>>
      }
    | undefined
  >
  /** Optional override for tests — defaults to {@link nextCode}. */
  allocateCode?: typeof nextCode
}

function buildCreateConfirmSummary(
  params: CreateRecordParams,
  relatedCache: RelatedOptionsCache,
): string {
  const displayValor = (campo: string, valor: string): string => {
    const hit = relatedCache
      .get(relatedCacheKey(params.entidade, campo))
      ?.find((c) => c.id === valor)
    return hit?.label ?? valor
  }

  const items = params.valores
  if (items.length === 0) return `Incluir ${params.entidade}`
  // Every field, always (decision H4): this is the only gate before an
  // irreversible write, and "Incluir Cor (6 campos)" confirms nothing. A long
  // chip is the chip's problem, not a reason to hide what is being written.
  const parts = items.map((i) => `${i.campo}: ${displayValor(i.campo, i.valor)}`)
  return `Incluir ${params.entidade} — ${parts.join('; ')}`
}

function entityHasRelations(entityName: string): boolean {
  try {
    return resolveCreatableFields(entityName).relations.length > 0
  } catch {
    return false
  }
}

/**
 * Build `data.findRelatedOptions` for queryable entities that have FK fields, plus
 * `data.createRecord` when `deps` is set and at least one entity is creatable.
 *
 * Request 0307: findRelated is the cheap path to filter findRecords by a related
 * name (resolve id → `partyId eq <id>`). It must cover queryable documents
 * (Pedido, Produto, …), not only silent-create catalogs.
 */
export function createAssistantCreateTools(
  queryable: readonly AssistantQueryableEntity[],
  relatedCache: RelatedOptionsCache,
  deps?: AssistantCreateToolsDeps,
): AssistantTool[] {
  const withRelations = queryable.filter((e) => entityHasRelations(e.entity))
  const creatable = queryable.filter((e) => e.creatable === true)
  if (withRelations.length === 0 && (!deps || creatable.length === 0)) return []

  const relatedByLabel = new Map(withRelations.map((e) => [e.label, e]))
  const relatedEnumLabels = withRelations.map((e) => e.label)
  const creatableByLabel = new Map(creatable.map((e) => [e.label, e]))
  const creatableEnumLabels = creatable.map((e) => e.label)
  const loadService = deps?.loadService ?? loadCreatableService
  const allocateCode = deps?.allocateCode ?? nextCode

  const tools: AssistantTool[] = []

  if (withRelations.length > 0) {
    const findRelated: AssistantTool<FindRelatedParams> = {
      name: 'data.findRelatedOptions',
      description:
        'Busca candidatos para um campo de relação (pelo rótulo). Não grava. ' +
        'Use o id (1) em data.findRecords no filtro do campo FK com eq ' +
        '(ex.: pedidos do cliente Silva → findRelatedOptions na entidade Pedido + campo Cliente + "Silva", ' +
        'depois findRecords com partyId/Cliente eq id), ou (2) em data.createRecord se a entidade for incluível. ' +
        'Não use caminho com ponto no filtro. ' +
        `campo = rótulo. busca = texto livre (contains). Até ${LOOKUP_CANDIDATE_CAP} resultados.`,
      parameters: {
        type: 'object',
        properties: {
          entidade: {
            type: 'string',
            ...(relatedEnumLabels.length > 0 ? { enum: relatedEnumLabels } : {}),
            description: 'Rótulo da entidade (enum) que tem o campo de relação.',
          },
          campo: {
            type: 'string',
            description: 'Rótulo do campo de relação.',
          },
          busca: {
            type: 'string',
            description: 'Trecho do nome/rótulo do registro alvo.',
          },
        },
        required: ['entidade', 'campo', 'busca'],
        additionalProperties: false,
      },
      kind: 'read',
      confirm: 'never',
      validate: (raw) => {
        if (!isRecord(raw)) return { ok: false, error: 'não entendi: parâmetros inválidos' }
        if (typeof raw.entidade !== 'string' || !raw.entidade.trim()) {
          return {
            ok: false,
            error: `não entendi: informe a entidade. Aceitas: ${relatedEnumLabels.join(', ')}`,
          }
        }
        const entry = relatedByLabel.get(raw.entidade)
        if (!entry) {
          return {
            ok: false,
            error: `entidade "${raw.entidade}" sem campo de relação nesta lista. Aceitas: ${relatedEnumLabels.join(', ')}`,
          }
        }
        if (typeof raw.campo !== 'string' || !raw.campo.trim()) {
          return { ok: false, error: 'não entendi: informe o campo (rótulo de relação)' }
        }
        if (typeof raw.busca !== 'string') {
          return { ok: false, error: 'não entendi: busca deve ser texto' }
        }
        const campo = raw.campo.trim()
        const busca = raw.busca.trim()
        if (!busca) {
          return { ok: false, error: 'não entendi: informe um termo em busca' }
        }

        const { relations } = resolveCreatableFields(entry.entity)
        const labels = relations.map((r) => r.label)
        if (!labels.includes(campo)) {
          const suggested = suggestLabels(labels, campo)
          const hint =
            suggested.length > 0
              ? `Campos de relação próximos: ${suggested.join(', ')}`
              : labels.length > 0
                ? `Campos de relação: ${labels.slice(0, 8).join(', ')}`
                : `Nenhum campo de relação em ${entry.label}`
          return { ok: false, error: `não entendi: campo "${campo}". ${hint}` }
        }

        return { ok: true, params: { entidade: raw.entidade, campo, busca } }
      },
      confirmSummary: (p) =>
        `Buscar opções de ${p.campo} em ${p.entidade} com "${p.busca}"`,
      run: async (params): Promise<AssistantToolResult> => {
        const entry = relatedByLabel.get(params.entidade)
        if (!entry) {
          return { ok: false, message: `Entidade desconhecida: "${params.entidade}".` }
        }
        const { relations } = resolveCreatableFields(entry.entity)
        const field = relations.find((r) => r.label === params.campo)
        if (!field) {
          return { ok: false, message: `não entendi: campo "${params.campo}"` }
        }

        const result = await searchLookupCandidates(
          field.targetEntity,
          field.displayField,
          params.busca,
        )
        if (!result.ok) {
          return { ok: false, message: result.error }
        }

        relatedCache.set(relatedCacheKey(params.entidade, params.campo), result.candidates)
        const n = result.candidates.length

        if (n === 0) {
          return {
            ok: true,
            message: `Nenhum registro casou com "${params.busca}" em ${params.campo}. Tente outro termo.`,
            data: { total: 0, returned: 0, truncated: false },
          }
        }

        const list = result.candidates.map(formatCandidateLine).join('; ')
        const truncNote = result.truncated
          ? ` (mostrando até ${LOOKUP_CANDIDATE_CAP}; refine a busca se precisar)`
          : ''
        const nextStep =
          entry.creatable === true
            ? 'Use o id em data.findRecords (filtro eq no FK) ou em data.createRecord.'
            : 'Use o id em data.findRecords no filtro do campo FK com eq.'
        return {
          ok: true,
          message:
            n === 1
              ? `1 opção para ${params.campo}: ${list}. ${nextStep}`
              : `${n} opções para ${params.campo}${truncNote}: ${list}. ${nextStep}`,
          data: {
            total: n,
            returned: n,
            truncated: result.truncated,
          },
        }
      },
    }
    tools.push(findRelated)
  }

  if (!deps || creatable.length === 0) return tools

  const createRecord: AssistantTool<CreateRecordParams> = {
    name: 'data.createRecord',
    description:
      'Inclui um registro novo (grava de verdade após confirmação do usuário). ' +
      'Um registro por chamada. entidade = rótulo incluível. valores = lista { campo, valor } ' +
      'com campo em rótulo; valor sempre texto. Relação: valor = id de data.findRelatedOptions ' +
      'daquele entidade+campo. Código autoNumber / auditoria / ciclo de vida: não informe. ' +
      'Campos: data.describeFields.',
    parameters: {
      type: 'object',
      properties: {
        entidade: {
          type: 'string',
          ...(creatableEnumLabels.length > 0 ? { enum: creatableEnumLabels } : {}),
          description: 'Rótulo da entidade incluível (enum).',
        },
        valores: {
          type: 'array',
          minItems: 1,
          items: {
            type: 'object',
            properties: {
              campo: {
                type: 'string',
                description: 'Rótulo do campo.',
              },
              valor: {
                type: 'string',
                description:
                  'Valor em texto; em campo de relação, o id de data.findRelatedOptions.',
              },
            },
            required: ['campo', 'valor'],
            additionalProperties: false,
          },
        },
      },
      required: ['entidade', 'valores'],
      additionalProperties: false,
    },
    kind: 'write',
    // H3 / request 0295: confirmation is the only gate before an irreversible write.
    // Do NOT set rememberable — "Sempre aplicar" must never skip this confirm.
    validate: (raw) => {
      if (!isRecord(raw)) return { ok: false, error: 'não entendi: parâmetros inválidos' }
      if (typeof raw.entidade !== 'string' || !raw.entidade.trim()) {
        return {
          ok: false,
          error: `não entendi: informe a entidade. Incluíveis: ${creatableEnumLabels.join(', ')}`,
        }
      }
      const entry = creatableByLabel.get(raw.entidade)
      if (!entry) {
        return {
          ok: false,
          error: `entidade "${raw.entidade}" não é incluível. Incluíveis: ${creatableEnumLabels.join(', ')}`,
        }
      }
      if (!Array.isArray(raw.valores) || raw.valores.length === 0) {
        return { ok: false, error: 'não entendi: informe ao menos um item em valores' }
      }

      const { writable, relations } = resolveCreatableFields(entry.entity)
      const fieldByLabel = new Map(writable.map((f) => [f.label, f]))
      const relationByLabel = new Map(relations.map((r) => [r.label, r]))
      const allLabels = [
        ...writable.map((f) => f.label),
        ...relations.map((r) => r.label),
      ]

      const parsed: CreateRecordItem[] = []

      for (let i = 0; i < raw.valores.length; i++) {
        const entryVal = raw.valores[i]
        if (!isRecord(entryVal)) {
          return { ok: false, error: `não entendi: item ${i + 1} de valores é inválido` }
        }
        if (typeof entryVal.campo !== 'string' || !entryVal.campo.trim()) {
          return { ok: false, error: `não entendi: item ${i + 1} precisa de campo (rótulo)` }
        }
        if (typeof entryVal.valor !== 'string') {
          return {
            ok: false,
            error: `não entendi: valor de "${entryVal.campo}" deve ser texto`,
          }
        }

        const campo = entryVal.campo.trim()
        const valor = entryVal.valor

        if (relationByLabel.has(campo)) {
          const candidates = relatedCache.get(relatedCacheKey(raw.entidade, campo))
          if (!candidates?.length) {
            return {
              ok: false,
              error: `use data.findRelatedOptions para "${campo}" em ${raw.entidade} antes de incluir`,
            }
          }
          const hit = candidates.find((c) => c.id === valor)
          if (!hit) {
            return {
              ok: false,
              error:
                `id "${valor}" não está entre as opções de "${campo}" em ${raw.entidade}. ` +
                `Busque de novo com data.findRelatedOptions.`,
            }
          }
          parsed.push({ campo, valor })
          continue
        }

        const field = fieldByLabel.get(campo)
        if (!field) {
          const suggested = suggestLabels(allLabels, campo)
          const hint =
            suggested.length > 0
              ? `Campos próximos: ${suggested.join(', ')}`
              : allLabels.length > 0
                ? `Campos disponíveis: ${allLabels.slice(0, 8).join(', ')}`
                : 'Nenhum campo preenchível nesta entidade'
          return { ok: false, error: `não entendi: campo "${campo}". ${hint}` }
        }

        const coerced = coerceFormFieldValue(field.descriptor, valor)
        if (!coerced.ok) return { ok: false, error: coerced.error }

        parsed.push({ campo, valor })
      }

      return { ok: true, params: { entidade: raw.entidade, valores: parsed } }
    },
    confirmSummary: (params) => buildCreateConfirmSummary(params, relatedCache),
    run: async (params): Promise<AssistantToolResult> => {
      const entry = creatableByLabel.get(params.entidade)
      if (!entry) {
        return { ok: false, message: `Entidade desconhecida: "${params.entidade}".` }
      }

      const orgId = deps.getOrgId()
      if (!orgId) {
        return {
          ok: false,
          message: 'Organização não selecionada. Selecione uma organização e tente de novo.',
        }
      }

      const { writable, relations, autoNumberField } = resolveCreatableFields(entry.entity)
      const fieldByLabel = new Map(writable.map((f) => [f.label, f]))
      const relationByLabel = new Map(relations.map((r) => [r.label, r]))

      const data: Record<string, unknown> = {}
      for (const item of params.valores) {
        const relation = relationByLabel.get(item.campo)
        if (relation) {
          const candidates = relatedCache.get(
            relatedCacheKey(params.entidade, item.campo),
          )
          if (!candidates?.length) {
            return {
              ok: false,
              message: `use data.findRelatedOptions para "${item.campo}" em ${params.entidade} antes de incluir`,
            }
          }
          const hit = candidates.find((c) => c.id === item.valor)
          if (!hit) {
            return {
              ok: false,
              message:
                `id "${item.valor}" não está entre as opções de "${item.campo}" em ${params.entidade}. ` +
                `Busque de novo com data.findRelatedOptions.`,
            }
          }
          data[relation.name] = hit.id
          continue
        }

        const field = fieldByLabel.get(item.campo)
        if (!field) {
          return { ok: false, message: `não entendi: campo "${item.campo}"` }
        }
        const coerced = coerceFormFieldValue(field.descriptor, item.valor)
        if (!coerced.ok) {
          return { ok: false, message: coerced.error }
        }
        data[field.name] = coerced.value
      }

      if (
        autoNumberField &&
        (data[autoNumberField.name] === undefined || data[autoNumberField.name] === null)
      ) {
        try {
          data[autoNumberField.name] = await allocateCode(orgId, {
            entity: entry.entity,
            field: autoNumberField.name,
            usePoint: entityUsePoint(entry.entity),
          })
        } catch (error) {
          rtLogService.warn('[assistant] data.createRecord autoNumber failed', {
            entity: entry.entity,
            error,
          })
          return {
            ok: false,
            message:
              `A numeração automática de ${autoNumberField.label} falhou. ` +
              `Informe o ${autoNumberField.label} em valores ou tente de novo.`,
          }
        }
      }

      const service = await loadService(entry.entity)
      if (!service) {
        return {
          ok: false,
          message: `Inclusão de ${entry.label} não está disponível neste momento.`,
        }
      }

      const reason = deps.getAuditReason()
      try {
        const created = await service.create(orgId, data, undefined, {
          audit: { source: 'assistant', reason },
        })
        const label = pickRecordLabel(created)
        return {
          ok: true,
          message: `Incluí ${entry.singularLabel} "${label}" (id=${created.id}).`,
          data: { id: created.id },
        }
      } catch (error) {
        if (error instanceof EntityValidationError) {
          return {
            ok: false,
            message: `Não incluí: ${translateValidationErrors(error.errors, entry.entity)}`,
          }
        }
        if (error instanceof ValidationException) {
          return {
            ok: false,
            message: `Não incluí: ${formatUniquenessError(error, entry.entity)}`,
          }
        }
        rtLogService.warn('[assistant] data.createRecord failed', {
          entity: entry.entity,
          error,
        })
        return {
          ok: false,
          message: `Não consegui incluir ${entry.singularLabel}. Tente de novo.`,
        }
      }
    },
  }

  tools.push(createRecord)
  return tools
}
